import {
  ChatTicket,
  ChatTicketCreateResult,
  ChatUpdatePayload,
  TicketETDDetails,
  TicketNotes,
  TicketNotesPayload,
  TicketNotesResponse,
  TicketTask,
} from "types/Models/chatTicket";
import qs, { stringify } from "qs";
import { Pagination } from "types/Models/pagination";
import { emptyApi } from "./emptyApi";

export const ticketApis = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    createChatTicket: build.mutation<
      ChatTicketCreateResult,
      {
        ticket_title: string;
        group_entity_id?: string;
        company_group_id: string;
        is_priced_ticket?: boolean;
        is_price_finalized?: boolean;
        ticket_price?: number;
        tag_ids?: string;
        assignee_profile_ids: string;
        is_non_chat_ticket: boolean;
        channel_url: string;
        message_id: string;
        create_ticket_channel?: boolean;
        ticket_channel_name?: string;
        etd_date?: string;
        reminder_date?: string;
        task_id?: string;
        priority: string;
        is_internal_ticket?: boolean;
        description?: string;
      }
    >({
      query: (payload) => {
        return {
          url: "/api/inkle/tickets/",
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ["ChatTickets", "TICKET_TASKS"],
    }),

    getChatTicketReports: build.query<
      string,
      {
        assignee_profile_ids: string | null;
        resolved_status: boolean | null;
        page_num: number;
        search_term?: string | null;
        tag_filter?: string | null;
        type_filters?: string | null;
        sort_by?: string | null;
        company_group_uuids?: string | null;
        action?: "EXPORT_TICKETS";
      }
    >({
      query: (queryParams) => {
        return {
          url: `/api/inkle/tickets/${stringify(queryParams, {
            skipNulls: true,
            addQueryPrefix: true,
          })}`,
        };
      },
      providesTags: ["ChatTickets"],
    }),

    getChatTickets: build.query<
      {
        tickets: ChatTicket[];
        unresolved_tickets: number;
      } & Pagination,
      {
        assignee_profile_ids: string | null;
        agent_ids: string | null;
        assigned_by_id?: string | null;
        priority?: string | null;
        resolved_status: boolean | null;
        page_num: number;
        search_term?: string | null;
        tag_filter?: string | null;
        type_filters?: string | null;
        etd_filters?: string | null;
        etd_date?: string | null;
        sort_by?: string | null;
        company_group_uuids?: string | null;
        task_name?: string | null;
        etd_start_date?: string | null;
        etd_end_date?: string | null;
        created_start_date?: string | null;
        created_end_date?: string | null;
        unassigned_spoc_tickets?: boolean | null;
        is_internal_ticket?: boolean | null;
        created_by_profile_ids?: string | null;
        resolved_start_date?: string | null;
        resolved_end_date?: string | null;
      }
    >({
      query: (params) => {
        let queryUrl = qs.stringify(params, {
          skipNulls: true,
          addQueryPrefix: true,
        });
        return {
          url: `/api/inkle/tickets/${queryUrl}`,
        };
      },
      providesTags: ["ChatTickets", "NOTES"],
    }),

    updateChatTicket: build.mutation<ChatTicket, ChatUpdatePayload>({
      query: ({
        assignee_profile_ids,
        resolved_status,
        ticket_id,
        ticket_title,
        ticket_price,
        is_priced_ticket,
        tag_ids,
        remove_tag_id,
        batch_tag_ids,
        is_price_finalized,
        create_ticket_channel,
        ticket_channel_name,
        priority,
        resolution_description,
      }) => {
        return {
          url: `/api/inkle/tickets/${ticket_id}/`,
          method: "put",
          body: {
            assignee_profile_ids,
            resolved_status,
            ticket_title,
            tag_ids,
            remove_tag_id,
            batch_tag_ids,
            ticket_price,
            is_priced_ticket,
            is_price_finalized,
            create_ticket_channel,
            ticket_channel_name,
            priority,
            resolution_description,
          },
        };
      },
      invalidatesTags: ["ChatTickets", "AGENT_ACTIONS", "TICKET_ETD_HISTORY"],
    }),

    getCustomerChatTickets: build.query<
      {
        tickets: ChatTicket[];
        tickets_count: number;
      } & Pagination,
      {
        resolved_status: boolean | null;
        page_num: number;
        group_id: string;
        search_term: string;
      }
    >({
      query: ({ resolved_status, page_num, group_id, search_term }) => {
        let queryUrl = qs.stringify(
          { page_num, resolved_status, search_term },
          {
            skipNulls: true,
            addQueryPrefix: true,
          }
        );
        return {
          url: `/api/inkle/tickets/group/${group_id}/${queryUrl}`,
        };
      },
      providesTags: ["CustomerChatTickets"],
    }),

    getSingleChatTickets: build.query<ChatTicket, { ticketUuid: string }>({
      query: ({ ticketUuid }) => `/api/inkle/tickets/${ticketUuid}/`,
      providesTags: ["ChatTickets"],
    }),

    createTicketNotes: build.mutation<TicketNotes, TicketNotesPayload>({
      query: ({ groupId, ticketId, description }) => {
        return {
          url: `/api/inkle/notes/group/${groupId}/tickets/${ticketId}/`,
          method: "post",
          body: {
            description,
          },
        };
      },
      invalidatesTags: ["TicketNotes"],
    }),

    getTicketNotes: build.query<
      TicketNotesResponse,
      { groupId: string; ticketId: string; page_num: number }
    >({
      query: ({ groupId, ticketId, page_num }) =>
        `/api/inkle/notes/group/${groupId}/tickets/${ticketId}?page_num=${page_num}`,
      providesTags: ["TicketNotes"],
    }),

    updateTicketNotes: build.mutation<
      TicketNotes,
      { noteId: string; description: string }
    >({
      query: ({ noteId, description }) => {
        return {
          url: `/api/inkle/notes/${noteId}/`,
          method: "put",
          body: {
            description,
          },
        };
      },
      invalidatesTags: ["TicketNotes"],
    }),

    deleteTicketNotes: build.mutation<any, { noteId: string }>({
      query: ({ noteId }) => {
        return {
          url: `/api/inkle/notes/${noteId}/`,
          method: "delete",
        };
      },
      invalidatesTags: ["TicketNotes"],
    }),

    getTicketById: build.query<
      ChatTicket,
      { groupId: string; ticketId: string }
    >({
      query: ({ groupId, ticketId }) =>
        `/api/inkle/tickets/${ticketId}/group/${groupId}`,
      providesTags: ["CustomerChatTickets"],
    }),
    getTicketSLAMetric: build.query<
      {
        average_resolution_time: {
          days: number;
          hours: number;
        };
        tickets_created_count: number;
        resolved_tickets_count: number;
      },
      { duration: number }
    >({
      query: ({ duration }) =>
        `/api/inkle/tickets/sla_metrics/?duration=${duration}`,
    }),

    escalateTicket: build.mutation<ChatTicket, { ticketId: string }>({
      query: ({ ticketId }) => {
        return {
          url: `/api/inkle/tickets/ticket/${ticketId}/escalate/`,
          method: "PUT",
        };
      },
      invalidatesTags: ["ChatTickets"],
    }),
    addTicketETD: build.mutation<
      ChatTicket,
      {
        ticketId: string;
        etd_date: string;
        reason?: string;
        inform_customer?: boolean;
        channel_url?: string;
        chat_message?: string;
      }
    >({
      query: ({ ticketId, ...rest }) => {
        return {
          url: `/api/inkle/tickets/ticket/${ticketId}/create_etd_history/ `,
          method: "POST",
          body: rest,
        };
      },
      invalidatesTags: ["ChatTickets", "TICKET_ETD_HISTORY"],
    }),
    getTicketETDHistory: build.query<TicketETDDetails[], { ticketId: string }>({
      query: ({ ticketId }) =>
        `/api/inkle/tickets/ticket/${ticketId}/get_etd_history/`,
      providesTags: ["TICKET_ETD_HISTORY"],
    }),
    getTicketTasks: build.query<TicketTask[], void>({
      query: () => `/api/inkle/tickets/tasks/`,
      providesTags: ["TICKET_TASKS"],
    }),

    getTicketChatDefaults: build.query<
      {
        assignee: {
          uuid: string;
          name: string;
          profile_url: string | null;
        } | null;
        title: string;
      },
      { company_group_id: string; chat_context?: string }
    >({
      query: ({ company_group_id, chat_context }) =>
        `/api/inkle/tickets/get-chat-defaults/?${qs.stringify({
          company_group_id,
          chat_context,
        })}`,
    }),
  }),
});

export const {
  useCreateChatTicketMutation,
  useGetChatTicketsQuery,
  useUpdateChatTicketMutation,
  useGetCustomerChatTicketsQuery,
  useGetSingleChatTicketsQuery,
  useCreateTicketNotesMutation,
  useGetTicketNotesQuery,
  useUpdateTicketNotesMutation,
  useDeleteTicketNotesMutation,
  useGetTicketByIdQuery,
  useLazyGetTicketByIdQuery,
  useLazyGetTicketNotesQuery,
  useLazyGetChatTicketReportsQuery,
  useGetTicketSLAMetricQuery,
  useEscalateTicketMutation,
  useAddTicketETDMutation,
  useGetTicketETDHistoryQuery,
  useGetTicketTasksQuery,
  useLazyGetTicketChatDefaultsQuery,
  useGetTicketChatDefaultsQuery,
} = ticketApis;
